// src/pages/InvoiceDetailPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  doc,
  onSnapshot,
  updateDoc,
  writeBatch,
  collection,
  serverTimestamp,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/styleUtils';
import styles from '../styles/InvoiceDetailPage.module.css';

const approvalRoles = ['Manager', 'Sales'];
const invoiceEditRoles = ['Manager', 'Sales', 'Accounts Manager'];
const currencyILS = (n) => new Intl.NumberFormat('en-IL', { style: 'currency', currency: 'ILS' }).format(n || 0);

const InvoiceDetailPage = () => {
  const { invoiceId } = useParams();
  const { currentUser, userRole } = useAuth();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Load invoice live
  useEffect(() => {
    if (!invoiceId) {
      setError('No invoice ID provided.');
      setLoading(false);
      return;
    }

    const invoiceRef = doc(db, 'invoices', invoiceId);
    const unsubscribe = onSnapshot(
      invoiceRef,
      (docSnap) => {
        if (!isMounted.current) return;
        if (docSnap.exists()) {
          setInvoice({ id: docSnap.id, ...docSnap.data() });
        } else {
          setError('Invoice not found.');
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching invoice:', err);
        if (isMounted.current) {
          setError('Failed to load invoice details.');
          setLoading(false);
        }
      }
    );

    return () => unsubscribe();
  }, [invoiceId]);

  // Approve/Reject
  const handleStatusUpdate = async (newStatus) => {
    if (!currentUser || isProcessing) return;

    const isApproving = newStatus === 'Approved';
    const confirmMessage = isApproving
      ? 'Are you sure you want to approve this invoice?'
      : 'Are you sure you want to reject this invoice? It will be sent back to the creator.';

    if (!window.confirm(confirmMessage)) return;

    setIsProcessing(true);
    setError('');

    const invoiceRef = doc(db, 'invoices', invoiceId);
    const batch = writeBatch(db);
    const managerName = currentUser.displayName || currentUser.email || 'Manager';

    try {
      batch.update(invoiceRef, {
        status: newStatus,
        updatedAt: serverTimestamp(),
        [`${newStatus.toLowerCase()}By`]: managerName,
        [`${newStatus.toLowerCase()}At`]: serverTimestamp(),
      });

      // Notify creator
      const notificationRef = doc(collection(db, 'notifications'));
      const message = `Your invoice #${invoice?.invoiceNumber ?? ''} for ${invoice?.clientName ?? 'client'} has been ${newStatus}.`;
      batch.set(notificationRef, {
        userId: invoice?.createdByUid,
        message,
        link: `/invoices/${invoiceId}`,
        timestamp: serverTimestamp(),
        isRead: false,
        createdBy: currentUser.uid,
        createdByName: managerName,
        type: isApproving ? 'invoice_approved' : 'invoice_rejected',
      });

      await batch.commit();
    } catch (err) {
      console.error('Error updating invoice status:', err);
      if (isMounted.current) setError('Failed to update the invoice status.');
    } finally {
      if (isMounted.current) setIsProcessing(false);
    }
  };

  // Delete invoice
  const handleDeleteInvoice = async () => {
    if (!currentUser || isProcessing) return;
    if (!window.confirm('This will permanently delete this invoice. Continue?')) return;

    setIsProcessing(true);
    setError('');
    try {
      await deleteDoc(doc(db, 'invoices', invoiceId));
      if (isMounted.current) {
        navigate('/invoices', {
          state: { message: `Invoice #${invoice?.invoiceNumber || ''} deleted.` },
        });
      }
    } catch (err) {
      console.error('Error deleting invoice:', err);
      if (isMounted.current) setError('Failed to delete invoice.');
    } finally {
      if (isMounted.current) setIsProcessing(false);
    }
  };

  const getStatusClass = (status) => {
    const statusKey = status?.toLowerCase().replace(/\s+/g, '') || 'default';
    return styles[`status-${statusKey}`] || styles.statusDefault;
  };

  if (loading) {
    return <div className={styles.loadingIndicator}>Loading Invoice...</div>;
  }
  if (error) {
    return <div className={styles.errorMessage}>{error}</div>;
  }
  if (!invoice) {
    return <div className={styles.errorMessage}>Invoice data could not be found or loaded.</div>;
  }

  const canEdit = invoiceEditRoles.includes(userRole) && invoice.status !== 'Paid';
  const canDelete = invoiceEditRoles.includes(userRole) && invoice.status !== 'Paid';
  const canTakeAction = approvalRoles.includes(userRole) && invoice.status === 'Pending Approval';

  const companyName = invoice.companyName || 'Grow Up';
  const companyLogo = invoice.companyLogoUrl || '';
  const companyAddress = invoice.companyAddress || '';
  const companyPhone = invoice.companyPhone || '';
  const companyEmail = invoice.companyEmail || '';
  const clientName = invoice.clientName || '';
  const clientAddress = invoice.clientAddress || '';
  const isPaid = invoice.status === 'Paid';

  return (
    <div className={styles.pageContainer}>
      {/* Screen header (hidden in print) */}
      <div className={`${styles.invoiceHeader} ${styles.noPrint}`}>
        <h1>Invoice #{invoice.invoiceNumber}</h1>
        <div className={styles.headerActions}>
          {canEdit && (
            <Link to={`/invoices/edit/${invoiceId}`} className={styles.editButton}>
              Edit Invoice
            </Link>
          )}
          {canDelete && (
            <button
              onClick={handleDeleteInvoice}
              className={styles.deleteButton}
              disabled={isProcessing}
              title="Delete this invoice"
            >
              Delete
            </button>
          )}
          <button onClick={() => window.print()} className={styles.actionButton}>
            Print
          </button>
          <Link to="/invoices" className={styles.actionButton}>
            Back to List
          </Link>
        </div>
      </div>

      {/* PRINT-FOCUSED LAYOUT */}
      <div className={styles.printArea}>
        {/* Watermark / Stamp */}
        {isPaid && <div className={styles.paidWatermark}>PAID</div>}

        {/* Header */}
        <div className={styles.printHeaderRow}>
          <div className={styles.brandBlock}>
            {companyLogo ? (
              <img src={companyLogo} alt="Company Logo" className={styles.brandLogo} />
            ) : (
              <div className={styles.brandLogoPlaceholder}>{companyName[0] || 'C'}</div>
            )}
            <div className={styles.brandText}>
              <h2 className={styles.brandName}>{companyName}</h2>
              {companyAddress && <p>{companyAddress}</p>}
              {(companyPhone || companyEmail) && (
                <p>
                  {companyPhone && <>Phone: {companyPhone}</>}{' '}
                  {companyPhone && companyEmail && ' • '}
                  {companyEmail && <>Email: {companyEmail}</>}
                </p>
              )}
            </div>
          </div>
          <div className={styles.invoiceMetaBox}>
            <h1 className={styles.invoiceTitle}>INVOICE</h1>
            <table className={styles.metaTable}>
              <tbody>
                <tr>
                  <td>Invoice #</td>
                  <td className={styles.metaValue}>{invoice.invoiceNumber}</td>
                </tr>
                <tr>
                  <td>Issue Date</td>
                  <td className={styles.metaValue}>
                    {invoice.issueDate ? formatDate(invoice.issueDate.toDate()) : '—'}
                  </td>
                </tr>
                <tr>
                  <td>Due Date</td>
                  <td className={styles.metaValue}>
                    {invoice.dueDate ? formatDate(invoice.dueDate.toDate()) : '—'}
                  </td>
                </tr>
                <tr>
                  <td>Status</td>
                  <td className={styles.metaValue}>
                    <span className={`${styles.statusBadge} ${getStatusClass(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Bill To + Amount Due */}
        <div className={styles.infoRow}>
          <div className={styles.billToBox}>
            <div className={styles.smallLabel}>Billed To</div>
            <div className={styles.billToName}>{clientName}</div>
            {clientAddress && <div className={styles.billToAddress}>{clientAddress}</div>}
          </div>
          <div className={styles.amountDueBox}>
            <div className={styles.smallLabel}>Amount Due</div>
            <div className={styles.amountDueValue}>{currencyILS(invoice.totalAmount)}</div>
          </div>
        </div>

        {/* Items */}
        <table className={styles.itemsTable}>
          <thead>
            <tr>
              <th>Description</th>
              <th className={styles.centerAlign}>Qty</th>
              <th className={styles.rightAlign}>Unit Price</th>
              <th className={styles.rightAlign}>Line Total</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.lineItems || []).map((item, index) => (
              <tr key={index}>
                <td>{item.description}</td>
                <td className={styles.centerAlign}>{item.quantity}</td>
                <td className={styles.rightAlign}>{currencyILS(item.unitPrice)}</td>
                <td className={styles.rightAlign}>{currencyILS(item.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className={styles.summaryRowWrap}>
            <div className={styles.notesBox}>
              <div className={styles.smallLabel}>Notes</div>
              <div className={styles.notesText}>{invoice.notes || 'No additional notes.'}</div>
            </div>
            <div className={styles.totalsBox}>
              <div className={styles.totalsLine}>
                <span>Subtotal</span>
                <span>{currencyILS(invoice.subtotal)}</span>
              </div>
              <div className={styles.totalsLine}>
                <span>Tax {typeof invoice.taxRate === 'number' ? `(${invoice.taxRate}%)` : ''}</span>
                <span>{currencyILS(invoice.taxAmount)}</span>
              </div>
              <div className={styles.totalsGrand}>
                <strong>Total</strong>
                <strong>{currencyILS(invoice.totalAmount)}</strong>
              </div>
            </div>
        </div>

        {/* Terms & signatures */}
        {(invoice.terms || true) && (
          <div className={styles.footerBlocks}>
            <div className={styles.termsBox}>
              <div className={styles.smallLabel}>Terms</div>
              <p className={styles.termsText}>
                {invoice.terms ||
                  'Payment due by the due date. Please include invoice number on all remittances.'}
              </p>
            </div>
            <div className={styles.signaturesBox}>
              <div className={styles.sig}>
                <div className={styles.sigLine} />
                <div className={styles.sigLabel}>Authorized Signature</div>
              </div>
              <div className={styles.sig}>
                <div className={styles.sigLine} />
                <div className={styles.sigLabel}>Client Signature</div>
              </div>
            </div>
          </div>
        )}

        <div className={styles.thanks}>
          Thank you for your business!
        </div>
      </div>

      {/* Manager actions (hidden in print) */}
      {canTakeAction && (
        <div className={`${styles.approvalActions} ${styles.noPrint}`}>
          <h3>Manager Actions</h3>
          <p>Please review this invoice. Once approved, it can be sent to the client.</p>
          <div className={styles.buttonGroup}>
            <button
              className={styles.rejectButton}
              onClick={() => handleStatusUpdate('Rejected')}
              disabled={isProcessing}
            >
              Reject
            </button>
            <button
              className={styles.approveButton}
              onClick={() => handleStatusUpdate('Approved')}
              disabled={isProcessing}
            >
              Approve
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetailPage;
